import { buildLinkSettingsQuery, parseLinkOffKeys } from '../../helpers/questionnaires/linkSettings'
import { buildShareQuestionnairePath } from '../../helpers/questionnaires/shareQuestionnairePath'
import buildShareUrl from '../../helpers/buildShareUrl'

const getQrcodeRoute = (locationParts) => locationParts[8] || locationParts[4]

// Only research qrcode paths carry a language: /:client/qrcode/:questionnaire/:moment/:research/:persona/:language/research
const getQrcodeLanguage = (locationParts) => (locationParts[8] === 'research' ? locationParts[7] : '') || ''

/**
 * De openbare pagina van een storytelling-deellink. Eén plek, zodat de
 * QR-code en 'Link kopiëren' (StorytellingShareLinks) hetzelfde adres geven.
 * De organisatie staat er alleen in voor de vorm van de url; de pagina haalt
 * haar uit de link (getStorytellingLink).
 * @param {string} client
 * @param {string} code - De code van de deellink
 * @returns {string}
 */
const getStorytellingOpenPath = (client, code) => `/${client}/storytellingopen/${code}/open`

// `search` is de query string van de QR-pagina: daar staan de instellingen die
// deze deellink uitzet (`?uit=…`), en die moet de QR-code ook dragen.
//
// `host` is de host waarop de app nu draait (`window.location.hostname`). De
// QR-code stond vast op app.deccos.nl, ook voor een organisatie op een eigen
// domein (ADR-029) en op localhost, terwijl 'Kopieer link' wél de huidige host
// gebruikt (testronde 2026-10-28, B1). Nu bouwen ze allebei via buildShareUrl.
const getQrcodePath = ({ client, questionnaire, measureMomentID, researchID, persona, route, languageCode, search }) => {
    switch (route) {
        case 'research':
            return `/${client}/questionnaireopen/${questionnaire}/${measureMomentID}/${researchID}/${persona}/open${languageCode ? `/${languageCode}` : ''}${buildLinkSettingsQuery(parseLinkOffKeys(search))}`
        // `questionnaire` is hier de code van de deellink.
        case 'storytelling':
            return getStorytellingOpenPath(client, questionnaire)
        case 'intakeform':
            return `/${client}/intakeformopen/${questionnaire}/open`
        case 'event':
            return `/${client}/eventopen/${questionnaire}/open`
        default:
            return ''
    }
}

/**
 * @param {object} params
 * @param {string} params.host - Host waarop de app nu draait (`window.location.hostname`)
 * @param {string} params.client
 * @param {string} params.questionnaire
 * @param {string} [params.measureMomentID]
 * @param {string} [params.researchID]
 * @param {string} [params.persona]
 * @param {string} params.route - 'research' | 'storytelling' | 'intakeform' | 'event'
 * @param {string} [params.languageCode]
 * @param {string} [params.search]
 * @returns {string} De volledige url voor de QR-code, of '' bij een onbekende route
 */
const getQrcodeValue = ({ host, ...params }) => {
    const path = getQrcodePath(params)
    return path ? buildShareUrl(host, path) : ''
}

// Terug naar de deelpagina, met wat daar gekozen was. Die pagina leest
// /sharequestionnaire/:vragenlijst/:onderzoek/:persona — het meetmoment hoort
// níét in het pad (testronde 2026-10-12, B2: dan stond het op de plek van het
// onderzoek en bleef de pagina leeg). Meetmoment en de instellingen per link
// reizen mee als query; ShareQuestionnaire zet ze terug.
// AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
const getQrcodeBackLink = ({ client, questionnaire, measureMomentID, researchID, persona, route, search }) => {
    switch (route) {
        case 'research':
            return buildShareQuestionnairePath({
                client,
                questionnaireID: questionnaire,
                researchID,
                respondentID: persona,
                measureMomentID,
                offKeys: parseLinkOffKeys(search),
            })
        case 'storytelling':
            return `/${client}/storytelling`
        case 'intakeform':
            return `/${client}/intakeforms`
        case 'event':
            return `/${client}/events/${questionnaire}`
        default:
            return `/${client}`
    }
}

// De briefpagina (Letter.jsx) leest zijn url op positie; deze twee zijn de enige
// plek waar die vorm staat, en een test toetst de rondgang.
//   onderzoek: /:client/letter/:vragenlijst/:meetmoment/:onderzoek/:persona/:taal
//   output:    /:client/letter/:registratieformulier/event
//   storytelling: /:client/letter/:deellinkcode/storytelling
// AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
const getLetterPath = ({ client, questionnaire, measureMomentID, researchID, persona, route, languageCode, search }) => {
    switch (route) {
        case 'research':
            return `/${client}/letter/${questionnaire}/${measureMomentID}/${researchID}/${persona}/${languageCode}${search || ''}`
        case 'event':
            return `/${client}/letter/${questionnaire}/event`
        case 'storytelling':
            return `/${client}/letter/${questionnaire}/storytelling`
        default:
            return ''
    }
}

/**
 * @param {string[]} parts - Location(): ['', client, 'letter', ...]
 * @returns {{client: string, questionnaire: string, measureMomentID: string, researchID: string, persona: string, route: 'research'|'event'|'storytelling', languageCode: string}}
 */
const parseLetterPath = (parts) => {
    const [, client, , questionnaire, measureMomentID, researchID, persona, languageCode] = parts
    if ((measureMomentID === 'event' || measureMomentID === 'storytelling') && !researchID) {
        return { client, questionnaire, measureMomentID: '', researchID: '', persona: '', route: measureMomentID, languageCode: '' }
    }
    return { client, questionnaire, measureMomentID, researchID, persona, route: 'research', languageCode: languageCode || '' }
}

// Terug van de brief naar de QR-pagina waar hij vandaan kwam.
const getQrcodePagePath = ({ client, questionnaire, measureMomentID, researchID, persona, route, languageCode, search }) => (route === 'event' || route === 'storytelling'
    ? `/${client}/qrcode/${questionnaire}/${route}`
    : `/${client}/qrcode/${questionnaire}/${measureMomentID}/${researchID}/${persona}/${languageCode}/research${search || ''}`)

export { getLetterPath, getQrcodeBackLink, getQrcodeLanguage, getQrcodePagePath, getQrcodeRoute, getQrcodeValue, getStorytellingOpenPath, parseLetterPath }
